import { BIOME } from "@eldrun/worldgen";

/**
 * Regras do áudio sem WebAudio (testáveis em Node): passa-baixa por Vida e água, vozes com
 * prioridade, corte por distância, superfície dos passos, emissor na borda do Cerco, estado da
 * música e a direção das legendas (doc 05, §Áudio procedural e §Feedback).
 */

/** Corte do passa-baixa no bus mestre (Hz): 22 kHz × razão de Vida abaixo de 75 %, 5 % submerso, abafado para quem assiste. */
export const LOWPASS = { open: 22_000, lowHp: 0.75, minHp: 900, submerged: 0.05, spectator: 1200 } as const;

export function lowpassHz(hpFraction: number, submerged: boolean, spectating: boolean): number {
  let hz: number = LOWPASS.open;
  const hp = Math.max(0, Math.min(1, hpFraction));
  if (hp < LOWPASS.lowHp) hz = Math.max(LOWPASS.minHp, LOWPASS.open * (hp / LOWPASS.lowHp));
  if (submerged) hz = Math.min(hz, LOWPASS.open * LOWPASS.submerged);
  if (spectating) hz = Math.min(hz, LOWPASS.spectator);
  return Math.round(hz);
}

/** Prioridade de uma voz: maior vence quando as 24 vozes estão ocupadas. */
export type VoicePriority = 0 | 1 | 2 | 3;
export const PRIORITY = { ambiente: 0, comum: 1, importante: 2, critico: 3 } as const;

export const VOICES = {
  max: 24,
  /** Corte por distância: 40 m para sons comuns, 100 m para chefes e avisos. */
  common: 40,
  boss: 100,
  /** HRTF só para o que é perto e importante; o resto, equalpower (mais barato). */
  hrtf: 15,
} as const;

/** Toca? (fora do corte, nem cria a voz). */
export function audible(distance: number, priority: VoicePriority): boolean {
  return distance <= (priority >= PRIORITY.importante ? VOICES.boss : VOICES.common);
}

interface Voice {
  id: number;
  priority: VoicePriority;
  distance: number;
  start: number;
}

/**
 * Vozes ocupadas (até 24): uma nova entra se houver vaga ou se puder roubar a de menor
 * prioridade (a mais longe e mais velha); senão, não toca.
 */
export class VoicePool {
  private readonly voices = new Map<number, Voice>();
  private next = 1;

  readonly max: number;

  constructor(max: number = VOICES.max) {
    this.max = max;
  }

  get size(): number {
    return this.voices.size;
  }

  /** Reserva uma voz; devolve o id e, se roubou, o id da voz a parar. */
  admit(priority: VoicePriority, distance: number, now: number): { id: number; steal?: number } | undefined {
    let steal: Voice | undefined;
    if (this.voices.size >= this.max) {
      for (const v of this.voices.values()) {
        if (v.priority > priority) continue;
        if (v.priority === priority && v.distance < distance) continue;
        if (!steal || v.priority < steal.priority || (v.priority === steal.priority && (v.distance > steal.distance || (v.distance === steal.distance && v.start < steal.start)))) steal = v;
      }
      if (!steal) return undefined;
      this.voices.delete(steal.id);
    }
    const id = this.next++;
    this.voices.set(id, { id, priority, distance, start: now });
    return steal ? { id, steal: steal.id } : { id };
  }

  release(id: number): void {
    this.voices.delete(id);
  }
}

/**
 * Sorteio da variação de cada som (F45): aleatório, sem repetir a última do mesmo som (com duas ou
 * mais). O gerador é injetável para os testes.
 */
export class VariationPicker {
  private readonly last = new Map<string, number>();
  private readonly random: () => number;

  constructor(random: () => number = Math.random) {
    this.random = random;
  }

  pick(key: string, count: number): number {
    if (count <= 1) return 0;
    const prev = this.last.get(key);
    // Sorteia entre as outras: a anterior nunca sai, e as outras têm a mesma chance.
    let i = Math.min(count - 2, Math.floor(this.random() * (count - 1)));
    if (prev === undefined) i = Math.min(count - 1, Math.floor(this.random() * count));
    else if (i >= prev) i++;
    this.last.set(key, i);
    return i;
  }
}

/**
 * Volume de um som: RMS da janela de 50 ms mais alta, em dBFS (a mesma medida do pipeline dos sons,
 * `tools/assets/src/sounds/pcm.ts`, para comparar amostra e síntese).
 */
export function loudestWindowDb(data: Float32Array, rate: number, seconds = 0.05): number {
  const w = Math.max(1, Math.min(data.length, Math.round(seconds * rate)));
  let acc = 0;
  let best = 0;
  for (let i = 0; i < data.length; i++) {
    acc += data[i]! * data[i]!;
    if (i >= w) acc -= data[i - w]! * data[i - w]!;
    if (i >= w - 1) best = Math.max(best, acc);
  }
  return best > 0 ? 10 * Math.log10(best / w) : -Infinity;
}

/**
 * A emenda de um loop (F46; a mesma medida do pipeline, `tools/assets/src/sounds/pcm.ts`): o degrau
 * entre a última amostra do loop (`loopEnd − 1`) e a primeira (`loopStart`), no pior canal, contra
 * o percentil 99 e o maior dos degraus de amostra a amostra em `window` amostras dos dois lados.
 * Emenda contínua: `jump` até o `p99` (as emendas cruas das fontes que estalam medem 1,8 a 5,7 × ele).
 */
export function seamJump(channels: readonly Float32Array[], loopStart: number, loopEnd: number, window: number): { jump: number; p99: number; max: number } {
  let jump = 0;
  const steps: number[] = [];
  for (const x of channels) {
    jump = Math.max(jump, Math.abs(x[loopStart]! - x[loopEnd - 1]!));
    for (let i = Math.max(loopStart, loopEnd - 1 - window); i < loopEnd - 1; i++) steps.push(Math.abs(x[i + 1]! - x[i]!));
    for (let i = loopStart; i < Math.min(loopEnd - 1, loopStart + window); i++) steps.push(Math.abs(x[i + 1]! - x[i]!));
  }
  steps.sort((a, b) => a - b);
  return { jump, p99: steps[Math.min(steps.length - 1, Math.floor(steps.length * 0.99))] ?? 0, max: steps[steps.length - 1] ?? 0 };
}

export type Surface = "grama" | "terra" | "areia" | "madeira" | "agua" | "pedra" | "lama";

/** Superfície dos passos: madeira sobre construção (pedra na laje da Caverna), água nadando, senão pelo bioma. */
export function surfaceAt(biome: number, onBox: boolean, swimming: boolean, onRock = false): Surface {
  if (swimming) return "agua";
  if (onBox) return onRock ? "pedra" : "madeira";
  switch (biome) {
    case BIOME.praia:
      return "areia";
    case BIOME.mangue:
      return "lama";
    case BIOME.mata:
      return "grama";
    case BIOME.cerrado:
      return "terra";
    case BIOME.serra:
      return "pedra";
    default:
      return "agua";
  }
}

/** Passada: um passo a cada tantos metros andados (mais largo correndo). */
export const stride = (speed: number): number => (speed > 5 ? 1.9 : 1.4);

/**
 * Emissor do fogo do Cerco (doc 05: técnica da zona da referência): no ponto da borda mais perto de
 * quem ouve; fora do anel, na posição dele.
 */
export function borderEmitter(center: { x: number; z: number }, radius: number, p: { x: number; z: number }): { x: number; z: number; outside: boolean } {
  const dx = p.x - center.x;
  const dz = p.z - center.z;
  const d = Math.hypot(dx, dz);
  if (d >= radius) return { x: p.x, z: p.z, outside: true };
  if (d < 1e-6) return { x: center.x + radius, z: center.z, outside: false };
  return { x: center.x + (dx / d) * radius, z: center.z + (dz / d) * radius, outside: false };
}

export type MusicState = "silencio" | "menu" | "lobby" | "dia" | "entardecer" | "noite" | "chefe" | "encanto" | "cerco" | "combate" | "travessia" | "vitoria" | "derrota" | "espectador";

export interface MusicInput {
  screen: "menu" | "lobby" | "carregando" | "jogando" | "fim";
  result?: "vitoria" | "derrota";
  night: boolean;
  dusk: boolean;
  /** Chefe vivo no mundo (a música dele não para até ele cair). */
  boss: boolean;
  challenge: boolean;
  raftStage?: string | undefined;
  treta: boolean;
  cercoClosing: boolean;
  spectating: boolean;
  /** Ameaça perto (F84, `CombatTracker`). */
  combat?: boolean;
}

/** Estado da trilha (doc 05, tabela "Estados de música"), do mais forte ao mais fraco. */
export function musicState(i: MusicInput): MusicState {
  if (i.screen === "menu") return "menu";
  if (i.screen === "lobby") return "lobby";
  if (i.screen === "carregando") return "silencio";
  if (i.screen === "fim") return i.result === "vitoria" ? "vitoria" : "derrota";
  if (i.raftStage === "travessia" || i.raftStage === "ancorada") return "travessia";
  if (i.boss) return "chefe";
  if (i.spectating) return "espectador";
  if (i.challenge) return "encanto";
  if (i.treta && i.cercoClosing) return "cerco";
  if (i.combat) return "combate";
  if (i.night) return "noite";
  if (i.dusk) return "entardecer";
  return "dia";
}

/** Crossfade entre estados (doc 05: 4–6 s; a vitória entra rápido e sobe em 4 s). */
export function crossfade(to: MusicState): { out: number; in: number } {
  if (to === "vitoria" || to === "derrota") return { out: 0.5, in: 4 };
  if (to === "chefe" || to === "cerco" || to === "combate") return { out: 2, in: 3 };
  return { out: 5, in: 5 };
}

/** Faixas gravadas (F84, ADR 0016): tocadas em streaming no lugar da partitura dos estados delas. */
export const TRACK_IDS = ["tema", "herois", "exploracao", "combate", "chefe"] as const;
export type TrackId = (typeof TRACK_IDS)[number];

/** Faixa de cada estado (decisão do dono, 2026-10-08); sem faixa, a partitura procedural. */
export const MUSIC_TRACK: Readonly<Record<MusicState, TrackId | undefined>> = {
  silencio: undefined,
  menu: "tema",
  lobby: "herois",
  dia: "exploracao",
  entardecer: "exploracao",
  noite: "exploracao",
  espectador: "exploracao",
  combate: "combate",
  cerco: "combate",
  encanto: "combate",
  chefe: "chefe",
  travessia: "chefe",
  vitoria: undefined,
  derrota: undefined,
};

export const trackFor = (state: MusicState): TrackId | undefined => MUSIC_TRACK[state];

/** Combate (F84): assombração viva a até 30 m alerta, perseguindo ou atacando; segura 8 s depois da última. */
export const COMBAT = { radius: 30, holdMs: 8000, states: ["alerta", "perseguindo", "preparando", "golpeando"] as readonly string[] } as const;

export class CombatTracker {
  private until = -Infinity;

  /** Quadro: houve ameaça agora? Devolve se a trilha de combate segue. */
  update(now: number, threat: boolean): boolean {
    if (threat) this.until = now + COMBAT.holdMs;
    return now < this.until;
  }

  reset(): void {
    this.until = -Infinity;
  }
}

/** Faixa pausada há até 60 s volta de onde parou (a exploração depois de uma briga); senão, do começo. */
export const MUSIC_RESUME_MS = 60_000;

export function resumeFrom(position: number, pausedAt: number | undefined, now: number): number {
  return pausedAt !== undefined && now - pausedAt <= MUSIC_RESUME_MS ? position : 0;
}

/** Lado de onde vem um som, para as legendas ("redemoinho à esquerda"). Yaw 0 olha para −z. */
export function soundSide(listener: { x: number; z: number; yaw: number }, source: { x: number; z: number }): "à frente" | "atrás" | "à esquerda" | "à direita" {
  const dx = source.x - listener.x;
  const dz = source.z - listener.z;
  const fx = -Math.sin(listener.yaw);
  const fz = -Math.cos(listener.yaw);
  // Direita da câmera: (−fz, fx) com x para a direita e z para trás.
  const ahead = dx * fx + dz * fz;
  const right = dx * -fz + dz * fx;
  if (Math.abs(ahead) >= Math.abs(right)) return ahead >= 0 ? "à frente" : "atrás";
  return right >= 0 ? "à direita" : "à esquerda";
}

/** Volumes por categoria (doc 05: geral, música, efeitos, ambiente, UI); a tela de opções é da F30. */
export interface Volumes {
  geral: number;
  musica: number;
  efeitos: number;
  ambiente: number;
  ui: number;
}
export const DEFAULT_VOLUMES: Volumes = { geral: 0.8, musica: 0.6, efeitos: 0.9, ambiente: 0.7, ui: 0.7 };
export const VOLUME_PREF = "volumes";

export function parseVolumes(raw: string | null | undefined): Volumes {
  try {
    const data = JSON.parse(raw ?? "") as Partial<Volumes>;
    const pick = (k: keyof Volumes) => (typeof data[k] === "number" && Number.isFinite(data[k]) ? Math.max(0, Math.min(1, data[k])) : DEFAULT_VOLUMES[k]);
    return { geral: pick("geral"), musica: pick("musica"), efeitos: pick("efeitos"), ambiente: pick("ambiente"), ui: pick("ui") };
  } catch {
    return { ...DEFAULT_VOLUMES };
  }
}
